import { useContext, useEffect, useMemo, useReducer, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";

import { ReportsIcon } from "../../Icons/Icons";
import { formatUnitNumber, fsaSort, sortAmount } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function ClaimsAnalysisReport() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  // State Management
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year || getCurrentReinsuranceYear());
  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  // Update global crop year
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: { key: "crop_year", value: currentYear },
    });
  }, [currentYear]);

  // Handle API Errors
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  // Fetch Data
  const { counties, producerFarms, producerFarmFieldCrops, isLoadingCounties } = useFarmController({
    crop_year: currentYear,
    filter,
    onError: handleError,
  });

  const [insuranceClaimsReportData, setInsuranceClaimsReportData] = useState([]);

  // Only call API when data is ready
  useEffect(() => {
    const loadInsurancePlanClaims = async () => {
      try {
        if (!producerFarmFieldCrops?.length || !globalState?.producer_token) return;

        const producerFilter = {
          producer_token: globalState.producer_token,
          is_active: true,
          reinsurance_year: currentYear,
        };

        const _insuranceClaims = await UsersApiHelper.users_search("InsurancePlanClaim", producerFilter, globalState);
        setInsuranceClaimsReportData(_insuranceClaims?.length ? _insuranceClaims : []);
      } catch (err) {
        setInsuranceClaimsReportData([]);
        handleError(err);
      }
    };

    if (producerFarmFieldCrops?.length) {
      loadInsurancePlanClaims();
    }
  }, [producerFarmFieldCrops, currentYear]);

  // Claims Report Data
  const claimsReportData = useMemo(() => {
    if (!producerFarmFieldCrops || !producerFarms) return [];

    return producerFarmFieldCrops.map((crop) => {
      const farm = producerFarms.find((farm) => farm.id === crop.producer_farm_id);

      // TODO calc new field values
      // const actualRevenue = crop.reported_acreage * FINAL_YIELD * HARVEST_PRICE;
      // const revenueGuarantee = crop.reported_acreage * GUARANTEE_PER_ACRE;
      // const difference = revenueGuarantee - actualRevenue;
      // const withShare = difference * crop.share_percent;

      const matchingClaim = insuranceClaimsReportData.find(
        (claim) =>
          claim.location_state_name === crop.location_state_name &&
          claim.location_county_name === crop.location_county_name &&
          claim.commodity_name === crop.commodity_name &&
          claim.practice_name === crop.practice_name
      );

      return {
        ...crop,
        farm_name: farm?.farm_name || "N/A",
        fsa_farm_number: farm?.fsa_farm_number || "N/A",
        tracts: _.uniq(farm?.ProducerFarmFieldCrops?.map((f) => f.fsa_tract_number)).join("/") || "N/A",
        fields: _.uniq(farm?.ProducerFarmFieldCrops?.map((f) => f.fsa_field_number)).join(",") || "N/A",
        name: farm?.farm_name || "N/A",
        other_share: farm?.other_person_sharing || "N/A",
        unit_number: formatUnitNumber(crop.basic_unit_number, crop.optional_unit_number),
        guarantee: "", //  TODO later scope!  blank for now
        final_yield: "", // TODO NEW FIELD that Afreen is working on
        claim: matchingClaim?.stage_name || "N/A",
        revenue_guarantee: "", // TODO later scope!  blank for now
        harvest_price: "", // TODO later scope!  blank for now
        actual_revenue: "", // TODO later scope!  blank for now
        difference: "", // TODO later scope!  blank for now
        with_share: "", // TODO later scope!  blank for now
      };
    });
  }, [producerFarmFieldCrops, producerFarms, insuranceClaimsReportData]);

  // Column Configuration
  const columnsDirective = [
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "fsa_farm_number", headerText: "Farm", width: 120, sortComparer: fsaSort },
    { field: "tracts", headerText: "Tracts", width: 150 },
    { field: "fields", headerText: "Fields", width: 150 },
    { field: "name", headerText: "Farm Name", width: 150 },
    { field: "commodity_name", headerText: "Crop", width: 120 },
    { field: "practice_name", headerText: "Practice", width: 150 },
    { field: "reported_acreage", headerText: "Certified Acres", width: 150 },
    { field: "share_percent", headerText: "Share %", width: 150, textAlign: "Right", format: "P0" },
    { field: "other_share", headerText: "Other Share", width: 150 },
    // { field: "unit_number", headerText: "Unit #", width: 120 },
    { field: "guarantee", headerText: "Guarantee", width: 120 },
    { field: "final_yield", headerText: "Final Yield", width: 120 },
    { field: "claim", headerText: "Claim", width: 150 },
    { field: "revenue_guarantee", headerText: "Revenue Guarantee", width: 180, sortComparer: sortAmount },
    { field: "harvest_price", headerText: "Harvest Price", width: 120, sortComparer: sortAmount },
    { field: "actual_revenue", headerText: "Actual Revenue", width: 150, sortComparer: sortAmount },
    { field: "difference", headerText: "Difference", width: 120, sortComparer: sortAmount },
    { field: "with_share", headerText: "With Share", width: 120, sortComparer: sortAmount },
  ];
  return (
    <>
      <WorkspaceHeader
        title="Claims Analysis"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <ReportViewer
        columnsDirective={columnsDirective}
        counties={counties}
        producerFarmFieldCrops={claimsReportData}
        isLoadingCounties={isLoadingCounties}
        reportType="claims"
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="claimsReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
      />
      {!isLoadingCounties && !claimsReportData?.length && (
        <EmptyCard text="No claims data found" secondaryText="Please check your filters or add more farm data." />
      )}
    </>
  );
}
